<template>
  <div class="gallery-wrapper">
    <Header ref="header">
      <template #header-slot>
        <BreadcrumbNavigation />
      </template>
    </Header>

    <div class="gallery-container" :class="{ 'with-content': !isLoadingInitial }">
      <!-- Loading state -->
      <div v-if="isLoadingInitial" class="gallery-loading">
        <div class="lds-ring"><div /><div /><div /><div /></div>
        <p>Loading dataset...</p>
      </div>
      <!-- Gallery content -->
      <GalleryGrid
        v-else
        ref="galleryGrid"
        :selectedImages="selectedImages"
        :imageList="imageList"
        :totalImageCount="totalImageCount"
        :imagesPerPage="imagesPerPage"
        :annotationSets="annotationSets"
      :doneFetch="doneFetch"
    >
    <template #header-first-row-start>
      <div class="gallery-tabs">
        <button
          v-if="hasSequences"
          class="gallery-tab"
          :class="{ 'gallery-tab--active': activeTab === 'sequences' }"
          @click="activeTab = 'sequences'"
        >
          Sequences ({{ cachedSequenceCount }})
        </button>
        <button
          class="gallery-tab"
          :class="{ 'gallery-tab--active': activeTab === 'images' }"
          @click="activeTab = 'images'"
        >
          Images ({{ cachedImageCount }})
        </button>
      </div>
    </template>
    <template #header-first-row-end>
      <div class="gallery-actions">
        <div class="gallery-actions__pagination">
          <PageControl
            v-model="currentPage"
            :totalPages="numPages"
          />
        </div>
        <div v-if="activeTab === 'images'" class="gallery-actions__toolbar">
          <button class="button" @click="showFilterDialog = true">
            Filter
            <SVGIcon
              :iconName="'expand_more'"
              :width="'18px'"
              :height="'18px'"
            />
          </button>
          <DisplayAnnotationsFilter
            v-model="annotationDisplaySettings"
            :annotationSets="annotationSets"
            :labels="currentLabels"
          />
          <DisplayImageDetails
            v-model="imageDisplaySettings"
            :hasSequences="hasSequences"
          />
          <GalleryActionsDropdown
            v-if="permissions?.dataset_write"
            :dataset="currentDataset"
            :selectedImages="selectedImages"
            :selectAll="selectAllFiles"
            :totalImageCount="totalImageCount"
            :pageImageCount="imageList ? imageList.length : 0"
            @remove-tag="handleRemoveTagsForSelectedImages"
            @add-tag="handleAddTagsForSelectedImages"
            @delete-images="openConfirmDeleteImageModal"
            @refresh-dataset="handleRefreshPage"
            @copy-image-modal="() => showCopyModal = true"
            @import-clicked="openUploadModal"
            @select-all-page="handleSelectAllPageFiles"
            @select-all="handleSelectAllFiles"
            @clear-selection="handleClearSelectAllFiles"
          />
        </div>
        <div v-else-if="activeTab === 'sequences'" class="gallery-actions__toolbar">
          <GalleryActionsDropdown
            v-if="permissions?.dataset_write"
            :dataset="currentDataset"
            :selectedImages="selectedImages"
            :selectAll="selectAllFiles"
            :totalImageCount="cachedSequenceCount"
            :pageImageCount="displayedImageList ? displayedImageList.length : 0"
            :mode="'sequences'"
            @delete-images="openConfirmDeleteSequencesModal"
          />
        </div>
      </div>
    </template>
    <template #header-second-row-start>
      <div class="gallery-actions">
        <!-- <button class="button" :class="{ 'inactive': false }">
            Copy Images
          </button> -->
      </div>
    </template>
    <template #header-second-row-end>
      <div class="gallery-actions">
      </div>
    </template>
    <template #header-third-row>
    </template>
    <template #grid-item>
      <!-- Sequences tab -->
      <template v-if="activeTab === 'sequences'">
        <SequenceCard
          v-for="seq in displayedImageList"
          :key="seq.id"
          :sequence="seq"
          :shouldShowSelector="true"
          :selected="selectedImages.some(s => s.id === seq.id) || selectAllFiles || selectAllPageFiles"
          @mousedown.stop=""
          @mouseup.stop=""
          @grid-item-clicked="displaySequence($event)"
          @update:selected="isSelected => toggleSequenceSelection(seq, isSelected)"
          @download="handleDownload($event)"
          @delete="handleDeleteSingle($event)"
          @edit-description="handleEditSequenceDescription($event)"
        />
      </template>
      <!-- Images tab -->
      <template v-else>
        <ImageCard
          v-for="imageObj in displayedImageList"
          :key="imageObj.id"
          :imageObj="imageObj"
          :annotations="imageObj.annotations"
          :annotationSets="annotationSets"
          :annotationDisplayType="colorAnnotationsBy"
          :filterAnnotationsBySets="displayAnnotationSets"
          :filterAnnotationsByLabelIndexes="displayLabels"
          :labels="currentLabels"
          :showName="showImageName"
          :showTags="showTags"
          :showLabels="true"
          :shouldShowSelector="true"
          :selected="selectedImages.some(img => img.id === imageObj.id) || selectAllFiles || selectAllPageFiles"
          v-bind="gridItemDisplayProp"
          @mousedown.stop=""
          @mouseup.stop=""
          @grid-item-clicked="displayImage($event)"
          @update:selected="isSelected => toggleImageSelection(imageObj, isSelected)"
        />
      </template>
    </template>
    <template #empty-state>
      <GalleryEmptyState
        v-if="!isFetchingImages"
        ref="galleryEmptyState"
        :datasetID="currentDatasetID"
        @import-clicked="$refs.uploadImagesModal.showModal();"
      />
    </template>
    <template #overlay>
      <div v-if="isFetchingSequences" class="gallery-sequence-loading">
        <div class="lds-ring"><div /><div /><div /><div /></div>
        <p>Loading sequences...</p>
      </div>
      <div v-if="isFetchingImages" class="gallery-image-loading">
        <div class="lds-ring"><div /><div /><div /><div /></div>
        <p>Loading images...</p>
      </div>
      <GalleryDropZone
        ref="galleryEmptyState"
        :datasetID="currentDatasetID"
        :showIcon="imageList && imageList.length > 0"
        @upload-complete="handleImageUploadComplete"
      />
    </template>
    <template #dropzone>
      <GalleryDropZone
        ref="galleryEmptyState"
        :datasetID="currentDatasetID"
        :showIcon="imageList && imageList.length > 0"
        @upload-complete="handleImageUploadComplete"
      />
    </template>
    <!-- <template #selectBox>
      <GalleryGridSelectBox
        :selectedImages="selectedImages"
        @multi-select="handleMultiSelect"
      />
    </template> -->
  </GalleryGrid>
  <ConfirmModal
    ref="confirmModal"
    :messageHeader="confirmMessageHeader"
    :message="confirmMessage"
    :buttonClass="buttonClass"
    :buttonText="buttonText"
    @confirmed="confirmFunction"
  />
  <UploadImagesModal
    ref="uploadImagesModal"
    :datasetID="currentDatasetID"
    :annotationSets="annotationSets"
    @upload-complete="handleImageUploadComplete"
    @upload-error="handleUploadError"
    @confirm-duplicate-upload="confirmDuplicateUpload"
  />
  <DatasetCopyModal
    v-if="showCopyModal"
    ref="copyDatasetModal"
    v-model:show="showCopyModal"
    :datasets="datasets"
    :source="currentDataset"
    :filters="imageFilterParams"
    :showFilters="false"
    :lockSource="true"
    :selectedImages="selectedImages"
    @copy-in-progress="handleClearSelectAllFiles"
  />
  <DatasetExportModal
    ref="datasetExportModal"
    :datasets="datasets"
    :sequences="sequences"
    :exportDataset="currentDataset"
  />
  <Teleport to="body">
    <Transition name="filter-drawer">
      <div v-if="showFilterDialog" class="filter-drawer-backdrop" @mousedown.self="showFilterDialog = false">
        <div class="filter-drawer">
          <div class="filter-drawer__header">
            <h2 class="mt-0 mb-0">Filters</h2>
            <IconButton
              :icon="'close'"
              :width="28"
              :height="28"
              @click="showFilterDialog = false"
            />
          </div>
          <div class="filter-drawer__content scrollbar">
            <DatasetFilter
              v-model="datasetFilters"
              :dataset="currentDataset"
              :annotationSets="annotationSets"
              :labels="currentLabels"
              :types="currentTypes"
              :collapsible="false"
              :showCopyFilteredImagesBtn="permissions && permissions.dataset_write"
              :showDeleteFilteredImagesBtn="permissions && permissions.dataset_write"
              @open-copy-filters="openCopyFilteredImagesModal"
              @delete-filtered-images="openConfirmDeleteFilteredImagesModal"
            />
            <div class="sort-by" style="margin-top: 12px;">
              <GalleryImageSort v-model:sortBy="sortBy" v-model:ascend="reverse" />
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
    </div>
  </div>
</template>

<script>
import DisplayAnnotationsFilter from '@/components/DatasetComponent/GalleryComponent/DisplayAnnotationsFilterV2.vue';
import GalleryImageSort from '@/components/DatasetComponent/GalleryComponent/GalleryImageSort.vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import GalleryGrid from '@/components/DatasetComponent/GalleryComponent/GalleryGrid.vue';
import UploadImagesModal from '@/components/DatasetComponent/UploadImagesModal.vue';
import ImageCard from '@/components/DatasetComponent/GalleryComponent/ImageCard.vue';
import SequenceCard from '@/components/DatasetComponent/GalleryComponent/SequenceCard.vue';
import GalleryEmptyState from '@/components/DatasetComponent/GalleryComponent/GalleryEmptyState.vue';
import GalleryDropZone from '@/components/DatasetComponent/GalleryComponent/GalleryDropZone.vue';
import SVGIcon from '@/components/SVGIcon.vue';
import IconButton from '@/components/IconButton.vue';
import Header from '@/components/Header.vue';
import ConfirmModal from '@/components/ConfirmModal.vue';
import DatasetCopyModal from "@/components/DatasetComponent/DatasetManagement/DatasetCopyModal.vue";
import DatasetExportModal from "@/components/DatasetComponent/DatasetManagement/DatasetExportModal.vue";
import DisplayImageDetails from '@/components/DatasetComponent/GalleryComponent/DisplayImageDetails.vue';
import useGallery from '@/composables/annotationTool/useGallery.js';
import DatasetFilter from '@/components/DatasetComponent/DatasetFilters/DatasetFilter.vue';
import GalleryActionsDropdown from "@/components/DatasetComponent/GalleryComponent/GalleryActionsDropdown.vue";
import PageControl from '@/components/DatasetComponent/GalleryComponent/PageControl.vue';
import FilterPopper from '@/components/FilterPopper.vue';
import BreadcrumbNavigation from '@/components/BreadcrumbNavigation.vue';
import equal from 'deep-equal';
import useTaskModal from '@/composables/useTaskModal.js';
import { encodeID } from '@/assets/js/utils.js';

import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';

const $toast = useToast();

export default {
  name: 'Gallery',
  props: {
    projectId: {
      type: [String, Number],
      default: null
    }
  },
  components: {
    GalleryGrid,
    DatasetFilter,
    DisplayImageDetails,
    DisplayAnnotationsFilter,
    ImageCard,
    SequenceCard,
    GalleryEmptyState,
    GalleryDropZone,
    UploadImagesModal,
    SVGIcon,
    IconButton,
    ConfirmModal,
    DatasetCopyModal,
    DatasetExportModal,
    GalleryImageSort,
    GalleryActionsDropdown,
    PageControl,
    FilterPopper,
    BreadcrumbNavigation,
    Header,
  },
  setup() {
    const {
      datasetFilters,
      annotationDisplaySettings,
      imageDisplaySettings,
      reverse,
      combineSequenceFrames,
      sortBy,
      currentDatasetID,
      internalCurrentDataset,
      currentPage,
      imagesPerPage,
      imageList,
      totalImageCount,
      getImagesParams,
      getImagesFilterParams,
      getImages,
      deleteFilteredImages,
      isFetchingImages,
    } = useGallery();
    combineSequenceFrames.value = false;

    const {
      pauseGetTasks,
      resumeGetTasks,
      isGettingTasks,
      getTasks,
    } = useTaskModal();

    return {
      datasetFilters,
      annotationDisplaySettings,
      imageDisplaySettings,
      reverse,
      sortBy,
      currentDatasetID,
      internalCurrentDataset,
      currentPage,
      imagesPerPage,
      imageList,
      totalImageCount,
      getImagesParams,
      getImagesFilterParams,
      getImages,
      deleteFilteredImages,
      isFetchingImages,
      combineSequenceFrames,
      pauseGetTasks,
      resumeGetTasks,
      isGettingTasks,
      getTasks,
    };
  },
  data() {
    return {
      dataConnect: new DatastoreConnect(this.$store.state.enterpriseServerUrl),
      dsID: null,
      imageListPromise: null,
      mapImageList: [],
      totalPagesCount: 0,
      dropHighlighted: false,
      gallerySelection: [],
      confirmMessage: null,
      confirmMessageHeader: null,
      buttonClass: "button-delete",
      buttonText: "",
      confirmFunction: null,
      showCopyModal: false,
      showCopyImageModal: false,
      showFilterDialog: false,
      permissions: null,
      selectedImage: null,
      sequences: [],
      showExportModal: false,
      doneFetch: true,
      selectedImages: [],
      selectAllPageFiles: false,
      selectAllFiles: false,
      hasSequences: false,
      activeTab: null,
      cachedImageCount: 0,
      cachedSequenceCount: 0,
      sequenceList: [],
      isFetchingSequences: false,
      localCurrentDataset: null,
      localDatasets: [],
      localCurrentProject: null,
      isInitializing: true,
      isLoadingInitial: true,
    };
  },
  computed: {
    canWrite: {
      get() {
        return this.permissions && this.permissions.dataset_write;
      },
    },
    currentProject: {
      get() {
        return this.localCurrentProject;
      },
      set(project) {
        this.localCurrentProject = project;
      },
    },
    datasets: {
      get() {
        return this.localDatasets;
      },
      set(datasets) {
        this.localDatasets = datasets;
      },
    },
    currentDataset: {
      get() {
        return this.localCurrentDataset;
      },
      set(dataset) {
        this.localCurrentDataset = dataset;
      },
    },
    currentLabels() {
      return this.currentDataset?.labels || [];
    },
    currentTypes() {
      if (this.currentDataset) {
        return this.currentDataset.annotation_types;
      }
      return [];
    },
    annotationSets: {
      get() {
        let sets = [];
        if (this.currentDataset) {
          sets = this.currentDataset?.annotation_sets;
        }
        if (!sets || (sets && sets.length === 0)) {
          this.getAnnotationSetsForDataset(this.currentDataset);
        }
        return sets;
      },
      set(sets) {
        if (this.currentDataset?.id) {
          const index = this.datasets.indexOf(this.datasets.find((d) => d.id === this.currentDataset.id));
          if (index >= 0) {
            this.$store.commit('datasets/setDatasetAnnotationSets', { index, sets });
          }
        }
      },
    },
    displayedImageList() {
      if (this.activeTab === 'sequences') {
        return this.sequenceList;
      }
      return this.imageList || [];
    },
    numPages() {
      if (this.activeTab === 'sequences') {
        return Math.ceil(this.cachedSequenceCount / this.imagesPerPage);
      }
      return Math.ceil(this.totalImageCount / this.imagesPerPage);
    },
    getAnnotationSetsParams() {
      if (!this.currentDataset) {
        return null;
      }

      const params = {
        dataset_id: this.currentDataset.id,
      };

      if (this.currentTab === 'annotations') {
        params.include_empty = true;
      }

      return params;
    },
    gridItemDisplayProp() {
      if (!this.datasetFilters?.displayAnnotationsFilter) {
        return {};
      }
      if (this.datasetFilters?.displayAnnotationsFilter?.displayType === 'byAnnotationSets') {
        return { filterAnnotationsBySets: this.datasetFilters.displayAnnotationsFilter.value };
      }
      if (this.datasetFilters?.displayAnnotationsFilter?.displayType === 'byAnnotationLabels') {
        return { filterAnnotationsByLabelIndexes: this.datasetFilters.displayAnnotationsFilter.value };
      }
      return {};
    },
    colorAnnotationsBy() {
      if (this.annotationDisplaySettings?.colorAnnotationsBy?.type) {
        return this.annotationDisplaySettings?.colorAnnotationsBy?.type;
      }
      return 'set';
    },
    displayAnnotationSets() {
      if (this.annotationDisplaySettings?.displayAnnotationSets) {
        return this.annotationDisplaySettings?.displayAnnotationSets.annotation_sets;
      }

      return [];
    },
    displayLabels() {
      if (this.annotationDisplaySettings?.displayLabels) {
        return this.annotationDisplaySettings?.displayLabels.labels;
      }
      return [];
    },
    showImageName() {
      if (this.imageDisplaySettings?.includes('showName')) {
        return true;
      }
      return false;
    },
    showSequences() {
      if (this.imageDisplaySettings?.includes('showSequences')) {
        return true;
      }
      return false;
    },
    showTags() {
      if (this.imageDisplaySettings?.includes('showTags')) {
        return true;
      }
      return false;
    },
    imageParams() {
      return this.getImagesParams();
    },
    imageFilterParams() {
      return this.getImagesFilterParams();
    },
    getImagesCombinedParams() {
      return { ...this.getImagesParams(), ...this.getImagesFilterParams() };
    },
    combinedParams() {
      return {
        // ...this.currentDatasetID,
        ...this.annotationDisplaySettings,
        ...this.imageDisplaySettings,
      };
    },
    minImageNumber() {
      return ((this.currentPage - 1) * this.imagesPerPage) + 1;
    },
    maxImageNumber() {
      const imgListLength = this.imageList ? this.imageList.length : 0;
      return ((this.currentPage - 1) * this.imagesPerPage) + imgListLength;
    },
    isPublic() {
      return this.currentProject?.is_public || false;
    },
  },
  watch: {
    '$route.query.dataset_id': {
      async handler(newVal) {
        if (newVal) {
          const idInt = parseInt(newVal);
          await this.getDatasetDetail(idInt);
        }
      },
    },
    '$route.query.dataset': {
      async handler(newVal) {
        if (newVal && !this.$route.query.dataset_id) {
          const idInt = parseInt(newVal);
          await this.getDatasetDetail(idInt);
        }
      },
    },
    '$route.params.datasetId': {
      async handler(newVal) {
        if (newVal) {
          const idInt = parseInt(newVal, 16);
          await this.getDatasetDetail(idInt);
          this.currentDatasetID = this.currentDataset ? this.currentDataset.id : 0;
          await this.checkHasSequences();
        }
      },
    },
    currentDataset: {
      handler() {
        this.currentPage = 1;
        this.currentDatasetID = this.currentDataset ? this.currentDataset.id : 0;
        this.internalCurrentDataset = this.currentDataset;
      },
    },
    getImagesCombinedParams: {
      deep: true,
      async handler(newVal, oldVal) {
        if (this.isInitializing) return;
        if (!equal(newVal, oldVal)) {
          this.imageListPromise = await new Promise((resolve, reject) => {
            this.doneFetch = false;
            this.getImages().then((imageList) => {
              this.imageList = imageList;
              this.selectedImages = [];
              this.selectAllPageFiles = false;
              this.selectAllFiles = false;
              resolve(imageList);
              this.doneFetch = true;
            }).catch((e) => {
              this.selectedImages = [];
              this.selectAllPageFiles = false;
              this.selectAllFiles = false;
              this.doneFetch = true;
            });
          });
        }
      },
    },
    combinedParams: {
      deep: true,
      async handler(newVal, oldVal) {
        if (this.isInitializing) return;
        this.$router.replace({
          name: this.$route.name,
          query: {
            ...this.$route.query,
            dataset: this.currentDatasetID,
            image_params: encodeURI(JSON.stringify({
              imagesPerPage: this.imagesPerPage,
              currentPage: this.currentPage > 0 ? this.currentPage : 1,
              sortBy: this.sortBy,
              reverse: this.reverse,
            })),
            filter_params: encodeURI(JSON.stringify(this.datasetFilters)),
            annotation_display: encodeURI(JSON.stringify(this.annotationDisplaySettings)),
            image_display: encodeURI(JSON.stringify(this.imageDisplaySettings)),
          },
          params: this.$route.params,
        });
      },
    },
    getAnnotationSetsParams: {
      deep: true,
      async handler(params) {
        if (params && params !== undefined) {
          this.annotationSets = await this.getAnnotationSetList(params);
        } else {
          this.annotationSets = [];
        }
      },
    },
    activeTab(newTab) {
      this.currentPage = 1;
      this.selectedImages = [];
      this.selectAllPageFiles = false;
      this.selectAllFiles = false;
      if (newTab === 'sequences') {
        this.fetchSequences();
      } else if (newTab === 'images') {
        // Fetch images when images tab is clicked
        this.doneFetch = false;
        this.imageList = [];
        this.getImages().then((imageList) => {
          this.imageList = imageList;
          this.doneFetch = true;
        }).catch(() => {
          this.doneFetch = true;
        });
      }
    },
    imageList: {
      immediate: true,
      handler(list) {
        if (!this.hasSequences && list?.some((img) => img.type === 'sequence')) {
          this.hasSequences = true;
        }
        if (this.activeTab === 'images') {
          this.cachedImageCount = this.totalImageCount;
        }
      },
    },
  },
  async mounted() {
    // Check for dataset ID in route params (new URL format) or query params (old format)
    const HexDsID = this.$route.params.datasetId || this.$route.query.dataset_id || this.$route.query.dataset;
    if (!HexDsID) {
      this.$toast.error('No dataset specified. Please select a dataset to view its gallery.', {
        position: 'top-right',
        duration: 5000,
      });
    }
      
    // Parse hex if it's from route params, otherwise treat as decimal
    this.dsID = this.$route.params.datasetId ? parseInt(HexDsID, 16) : parseInt(HexDsID);
    await this.getDatasetDetail(this.dsID);
    this.currentDatasetID = this.currentDataset ? this.currentDataset.id : 0;
    this.internalCurrentDataset = this.currentDataset;

    if (this.$route.query.image_params) {
      const image_params = JSON.parse(decodeURI(this.$route.query.image_params));
      this.imagesPerPage = image_params.imagesPerPage;
      this.currentPage = image_params.currentPage;
      this.sortBy = image_params.sortBy;
      this.reverse = image_params.reverse;
    }

    if (this.$route.query.filter_params) {
      this.displayFilters = JSON.parse(decodeURI(this.$route.query.filter_params));
    }

    if (this.$route.query.annotation_display) {
      this.annotationDisplaySettings = JSON.parse(decodeURI(this.$route.query.annotation_display));
    }

    if (this.$route.query.image_display) {
      this.imageDisplaySettings = JSON.parse(decodeURI(this.$route.query.image_display));
    }

    this.GetPermissionsForCurrentUser();
    // Always check sequences after dataset is loaded
    await this.getSequences(this.dsID);
    // Mark initialization complete
    this.isLoadingInitial = false;
    this.isInitializing = false;
  },
  methods: {
    async getSequences(dsID) {
      if (!dsID) return;
      const resp = await this.dataConnect.getSequences({
        dataset_id: dsID,
      }).catch(() => null);
      const seqList = resp?.result || [];
      if (seqList.length > 0) {
        this.hasSequences = true;
        this.cachedSequenceCount = seqList.length;
        // Show sequences tab if sequences exist
        this.activeTab = 'sequences';
      } else {
        // No sequences, show images tab
        this.activeTab = 'images';
      }
      // Cache image count from flat count
      const flatResp = await this.dataConnect.getFilteredImages({
        only_count: true,
        combine_sequence_frames: false,
        images_filter: { dataset_id: this.currentDatasetID },
      }).catch(() => null);
      this.cachedImageCount = flatResp?.result || 0;
    },
    async fetchSequences() {
      if (!this.currentDatasetID) return;
      this.isFetchingSequences = true;
      // Fetch sequence metadata from sequences.list
      const resp = await this.dataConnect.getSequences({
        dataset_id: this.currentDatasetID,
      }).catch((e) => { console.error('[fetchSequences] getSequences error:', e); return null; });
      const seqList = resp?.result || [];
      this.cachedSequenceCount = seqList.length;
      if (seqList.length === 0) {
        this.sequenceList = [];
        this.isFetchingSequences = false;
        return;
      }
      // Fetch first-frame image IDs via image.list.filter with combine_sequence_frames
      const imgResp = await this.dataConnect.getFilteredImages({
        combine_sequence_frames: true,
        images_filter: { dataset_id: this.currentDatasetID },
        image_files_filter: {},
        annotations_filter: null,
        limit: 1000,
        offset: 0,
      }).catch((e) => { console.error('[fetchSequences] getFilteredImages error:', e); return null; });
      if (imgResp?.error) {
        console.error('[fetchSequences] API error:', imgResp.error);
      }
      const imgList = Array.isArray(imgResp?.result) ? imgResp.result : [];
      // Build lookup: sequence_id -> first frame image id
      const seqImageMap = {};
      imgList.forEach((img) => {
        if (img.sequence_id && !seqImageMap[img.sequence_id]) {
          seqImageMap[img.sequence_id] = img.id;
        }
      });
      // Merge first image ID into sequence objects
      this.sequenceList = seqList.map((seq) => ({
        ...seq,
        _firstImageId: seqImageMap[seq.id] || null,
      }));
      this.isFetchingSequences = false;
    },
    async getAnnotationSetsForDataset(dataset) {
      if (dataset) {
        const resp = await this.dataConnect.getAnnotationSets({
          dataset_id: dataset.id,
          get_num_annotations: true,
          get_num_images: true,
          get_labels: true,
        })
          .catch((error) => {
            console.log('Failed to retrieve sets:', error);
          });
        if (resp && !resp.error && resp.result) {
          this.annotationSets = resp.result;
        }
      }
    },
    async GetPermissionsForCurrentUser() {
      const user = this.$store.state?.user?.user;
      if (this.currentDataset && user) {
        await this.dataConnect.getProjectAccessForSingleUser({
          project_id: this.currentDataset.project_id,
          username: user.username,
        })
          .then((data) => {
            if (data.result) {
              this.permissions = data.result;
            }
          })
          .catch((e) => console.log(e));
      } else {
        this.permissions = null;
      }
    },
    async getAnnotationSetList(params) {
      const annotationSets = await this.dataConnect.getAnnotationSets(params)
        .catch((error) => {
          // TODO: handle error
          console.log(error);
        });
      if (annotationSets.error || !annotationSets.result) {
        // TODO: handle error
        return {};
      }
      return annotationSets.result;
    },
    // async getLabelList() {
    //   const labels = await this.dataConnect.getLabelList({ dataset_id: this.currentDataset.id })
    //     .catch((error) => {
    //       // TODO: handle error
    //       console.log(error);
    //     });
    //   return labels.result;
    // },
    handleGallerySelectionChanged(selection) {
      this.gallerySelection = selection;
    },
    async handleCopyCompleted() {
      // Datasets are loaded directly from API, no need to refresh from store
    },
    async handleAnnotationSetCreated() {
      this.annotationSets = await this.getAnnotationSetList();
    },
    openConfirmDeleteFilteredImagesModal() {
      this.confirmMessage = `Are you sure you want to delete all filtered images`;
      this.confirmMessageHeader = 'Delete Filtered Images';
      this.buttonClass = "button-delete";
      this.buttonText = 'Delete';
      this.$refs.confirmModal.showModal();
      this.confirmFunction = () => this.deleteFilteredImages();
    },
    openConfirmDeleteImageModal() {
      const filtered = this.selectedImages.reduce((acc, item) => {
        if (item.sequence_id && item.sequence_id > 0 && item.type === 'sequence') {
          acc.sequence_ids.push(item.sequence_id);
        } else if (item.id > 0 && item.type === 'image') {
          acc.image_ids.push(item.id);
        } else {
          // also assume non_sequence or non_image to be images
          acc.image_ids.push(item.id);
        }
        return acc;
      }, { sequence_ids: [], image_ids: [] });
      let numImagesToDelete = 0;
      if (this.selectAllFiles) {
        numImagesToDelete = this.totalImageCount;
      } else {
        numImagesToDelete = this.selectedImages.length;
      }
      this.confirmMessage = `Are you sure you want to permanently delete ${numImagesToDelete} item(s)?`;
      this.confirmMessageHeader = `Deleting Items`;
      this.buttonClass = "button-delete";
      this.buttonText = 'Delete';
      this.$refs.confirmModal.showModal();
      this.confirmFunction = () => this.deleteFilteredImages2(filtered.image_ids, filtered.sequence_ids);
    },
    handleDeleteSingle(obj) {
      // Detect if from sequences.list (has no_of_images) or image.list.filter (has sequence_id + type)
      const isSeqFromList = obj.no_of_images !== undefined;
      const isSeqFromImages = obj.sequence_id && obj.sequence_id > 0 && obj.type === 'sequence';
      const isSeq = isSeqFromList || isSeqFromImages;
      this.confirmMessage = `Are you sure you want to permanently delete this ${isSeq ? 'sequence' : 'image'}?`;
      this.confirmMessageHeader = `Deleting ${isSeq ? 'Sequence' : 'Image'}`;
      this.buttonClass = "button-delete";
      this.buttonText = 'Delete';
      this.$refs.confirmModal.showModal();
      const seqIds = isSeq ? [isSeqFromList ? obj.id : obj.sequence_id] : [];
      const imgIds = isSeq ? [] : [obj.id];
      this.confirmFunction = () => {
        this.deleteFilteredImages2(imgIds, seqIds).then(() => {
          if (isSeq && this.activeTab === 'sequences') {
            this.fetchSequences();
          }
        });
      };
    },
    async deleteFilteredImages2(image_ids, sequence_ids) {
      if (!image_ids.length > 0 && !sequence_ids.length > 0 && !this.selectAllFiles) {
        return;
      }

      const params = {
        ...this.imageFilterParams,
        dataset_id: this.currentDatasetID,
      };

      // Filter by selectect images/sequences
      if (!this.selectAllFiles) {
        if (this.imageFilterParams.images_filter && image_ids.length > 0) {
          this.imageFilterParams.images_filter.image_ids = image_ids;
        }
        if (this.imageFilterParams.images_filter && sequence_ids.length > 0) {
          this.imageFilterParams.images_filter.sequence_ids = sequence_ids;
        }
      }

      this.dataConnect.deleteFilteredImages2(params)
        .then((resp) => {
          if (resp.error !== undefined) {
            throw Error(resp.error.message);
          }
          this.getImages().then((imageList) => {
            this.imageList = imageList;
          });
        })
        .catch((error) => {
          const $toast = useToast();
          $toast.open({
            queue: true,
            duration: 5000,
            message: error.message,
            type: 'error',
            position: 'top',
          });
        });
      this.selectedImages = [];
      this.selectAllPageFiles = false;
      this.selectAllFiles = false;
    },
    openConfirmDeleteSequencesModal() {
      const seqIds = this.selectedImages
        .filter((item) => item.id && item.no_of_images !== undefined)
        .map((item) => item.id);
      
      if (seqIds.length === 0) {
        return;
      }

      this.confirmMessage = `Are you sure you want to permanently delete ${seqIds.length} sequence(s)?`;
      this.confirmMessageHeader = 'Deleting Sequences';
      this.buttonClass = 'button-delete';
      this.buttonText = 'Delete';
      this.$refs.confirmModal.showModal();
      this.confirmFunction = () => {
        this.deleteFilteredImages2([], seqIds).then(() => {
          this.selectedImages = [];
          this.selectAllPageFiles = false;
          this.selectAllFiles = false;
          this.fetchSequences();
        });
      };
    },
    async deleteImageFromDataset(sequence_ids, image_ids) {
      const params = {
        dataset_id: this.currentDataset.id,
        sequence_ids,
        image_ids,
      };
      this.dataConnect.deleteImageFromDataset(params)
        .then((data) => {
          if (!data || data.error || !data.result) {
            const $toast = useToast();
            $toast.open({
              queue: true,
              duration: 5000,
              message: data.error.message,
              type: 'error',
              position: 'top',
            });
          } else {
            this.getImages().then((imageList) => {
              this.imageList = imageList;
            });
          }
        });
      this.selectedImages = [];
      this.selectAllPageFiles = false;
      this.selectAllFiles = false;
    },
    displayImage(imageObj) {
      const isCurrentRoutePublic = this.$route.name.includes('public');
      const hasNewFormat = !!this.$route.params.datasetId;
      
      if (hasNewFormat) {
        // Use new gallery viewer route with hex-encoded dataset ID
        this.$router.push({
          name: `${isCurrentRoutePublic ? 'public.' : ''}datasets.gallery.new.viewer`,
          params: { datasetId: this.$route.params.datasetId },
          query: {
            image_id: encodeURI(JSON.stringify(imageObj.id)),
            annotationDisplaySettings: encodeURI(JSON.stringify(this.annotationDisplaySettings)),
            showSequences: encodeURI(this.imageDisplaySettings.includes('showSequences')),
          },
        });
      } else {
        // Use old gallery viewer route with projectId
        const projectId = this.currentProject ? encodeID(this.currentProject.id, 'p') : '';
        this.$router.push({
          name: `${isCurrentRoutePublic ? 'public.' : ''}datasets.gallery.viewer`,
          params: { projectId: isCurrentRoutePublic ? '' : projectId },
          query: {
            dataset: this.currentDatasetID,
            image_id: encodeURI(JSON.stringify(imageObj.id)),
            annotationDisplaySettings: encodeURI(JSON.stringify(this.annotationDisplaySettings)),
            showSequences: encodeURI(this.imageDisplaySettings.includes('showSequences')),
          },
        });
      }
    },
    displaySequence(seq) {
      // Navigate to viewer with the first frame of the sequence
      if (seq._firstImageId) {
        const isCurrentRoutePublic = this.$route.name.includes('public');
        const hasNewFormat = !!this.$route.params.datasetId;
        
        if (hasNewFormat) {
          // Use new gallery viewer route with hex-encoded dataset ID
          this.$router.push({
            name: `${isCurrentRoutePublic ? 'public.' : ''}datasets.gallery.new.viewer`,
            params: { datasetId: this.$route.params.datasetId },
            query: {
              image_id: encodeURI(JSON.stringify(seq._firstImageId)),
              annotationDisplaySettings: encodeURI(JSON.stringify(this.annotationDisplaySettings)),
              showSequences: 'true',
            },
          });
        } else {
          // Use old gallery viewer route with projectId
          const projectId = this.currentProject ? encodeID(this.currentProject.id, 'p') : '';
          this.$router.push({
            name: `${isCurrentRoutePublic ? 'public.' : ''}datasets.gallery.viewer`,
            params: { projectId: isCurrentRoutePublic ? '' : projectId },
            query: {
              dataset: this.currentDatasetID,
              image_id: encodeURI(JSON.stringify(seq._firstImageId)),
              annotationDisplaySettings: encodeURI(JSON.stringify(this.annotationDisplaySettings)),
              showSequences: 'true',
            },
          });
        }
      }
    },
    toggleSequenceSelection(seq, isSelected) {
      if (isSelected) {
        if (!this.selectedImages.some((s) => s.id === seq.id)) {
          this.selectedImages = [...this.selectedImages, seq];
        }
      } else {
        this.selectedImages = this.selectedImages.filter((s) => s.id !== seq.id);
      }
      this.selectAllPageFiles = false;
      this.selectAllFiles = false;
    },
    openUploadModal() {
      this.$refs.uploadImagesModal.showModal();
    },
    handleImageUploadComplete() {
      this.imageListPromise = new Promise((resolve, reject) => {
        this.getImages().then((imageList) => {
          this.imageList = imageList;
          resolve(imageList);
        });
      });
      this.$refs.uploadImagesModal.closeModal();
    },
    async handleUploadError(data) {
      this.confirmMessage = data.confirmMessage;
      this.confirmMessageHeader = data.confirmMessageHeader;
      this.buttonText = "";
      this.$refs.confirmModal.showModal();
    },
    openCopyFilteredImagesModal() {
      this.showCopyModal = true;
    },
    async confirmCopy(args) {
      this.confirmMessage = `Destination dataset contains ${args.duplicate_count} ${args.duplicate_count === 1 ? 'image' : 'images'} with the same name. 
      ${args.duplicate_count === 1 ? 'This image' : 'These images'} will be ignored.`;
      this.confirmNote = "";
      this.confirmMessageHeader = 'Duplicate files';
      this.buttonClass = null;
      this.buttonText = "Confirm";
      this.buttonClass = "";
      this.confirmFunction = async () => { await this.handleConfirmCopy(args.fullParams); };
      this.$refs.confirmModal.showModal();
    },
    async handleConfirmCopy(params) {
      await this.dataConnect.confirmCopySequences(params)
        .then((resp) => {
          if (resp.error !== undefined) {
            throw Error(resp.error.message);
          } else if (resp.result) {
            const destDS = this.datasets.find((e) => e.id === params.dest_dataset_id);
            if (!destDS) {
              $toast.open({
                queue: true,
                duration: 5000,
                message: `Copying in progress. Refresh the page to view progress.`,
                type: "success",
                position: "top",
              });
              this.refreshDatasetCard(destDS);
            } else {
              $toast.open({
                queue: true,
                duration: 5000,
                message: `Copying in progress.`,
                type: "success",
                position: "top",
              });
            }

            this.getTasks();
            if (!this.isGettingTasks) {
              this.resumeGetTasks();
            }

            this.showCopyModal = false;
          }
        })
        .catch((error) => {
          $toast.open({
            queue: true,
            duration: 5000,
            message: `Failed to copy: ${error.message}`,
            type: "error",
            position: "top",
          });
        });
    },
    handleCopy(imageObj) {
      if (!this.selectedImages.includes(imageObj)) {
        this.selectedImages.push(imageObj);
      }
      this.showCopyModal = true;
    },
    handleDownload(obj) {
      if (obj.type === 'sequence') {
        // From image.list.filter (old path)
        this.sequences = [{ id: obj.sequence_id, uuid: obj.uuid, name: obj.name }];
      } else if (obj.no_of_images !== undefined) {
        // From sequences.list
        this.sequences = [{ id: obj.id, uuid: obj.uuid, name: obj.name }];
      }
      this.$refs.datasetExportModal.showModal();
    },
    handleSelectAllPageFiles() {
      if (!this.selectAllPageFiles) {
        this.selectedImages = this.imageList;
        this.selectAllPageFiles = true;
        this.selectAllFiles = false;
      } else {
        this.selectedImages = [];
        this.selectAllPageFiles = false;
        this.selectAllFiles = false;
      }
    },
    handleSelectAllFiles() {
      this.selectAllFiles = true;
      this.selectedImages = [];
    },
    handleClearSelectAllFiles() {
      this.selectedImages = [];
      this.selectAllPageFiles = false;
      this.selectAllFiles = false;
    },
    toggleImageSelection(imageObj, isSelected) {
      if (isSelected) {
      // Add if not already in
        if (!this.selectedImages.some((img) => img.id === imageObj.id)) {
          this.selectedImages = [...this.selectedImages, imageObj];
        }
      } else {
      // Remove
        this.selectedImages = this.selectedImages.filter(
          (img) => img.id !== imageObj.id,
        );
      }
      this.selectAllPageFiles = false;
      this.selectAllFiles = false;
    },
    handleMultiSelect(IDs) {
      this.selectedImages = this.imageList.filter((e) => IDs.includes(e.id));
    },
    handleRemoveTagsForSelectedImages(tag_name) {
      const selectedImageIds = this.selectedImages.map((e) => e.id);
      this.imageList.forEach((image, index) => {
        if (selectedImageIds.includes(image.id)) {
          this.imageList[index].tags = this.imageList[index].tags.filter((e) => e.name !== tag_name);
        }
      });
    },
    handleAddTagsForSelectedImages(tag_name) {
      const selectedImageIds = this.selectedImages.map((e) => e.id);
      this.imageList.forEach((image, index) => {
        if (selectedImageIds.includes(image.id)) {
          this.imageList[index].tags.push({ name: tag_name });
        }
      });
    },
    handleRefreshPage() {
      this.imageListPromise = new Promise((resolve, reject) => {
        this.getImages().then((imageList) => {
          this.imageList = imageList;
          resolve(imageList);
        });
      });
    },
    async getDatasetDetail(dataset_id) {
      if (dataset_id) {
        const params = {
          dataset_id,
          get_num_images: true,
          get_labels: true,
          get_num_labels: true,
          get_annotation_sets: true,
          get_groups: true,
          get_docker_tasks: true,
        };
        await this.dataConnect.getDatasetById(params)
          .then((resp) => {
            if (!resp.error) {
              const temp = resp.result;
              this.localCurrentDataset = temp;
            } else {
              let msg = resp.error?.message || String(resp.error);
              if (msg.includes("no rows")) {
                msg = "Dataset does not exist.";
              }
              $toast.open({
                queue: true,
                duration: 5000,
                message: `Failed to retrieve dataset: ${msg}`,
                type: "error",
                position: "top",
              });
            }
          })
          .catch((error) => {
            let msg = error?.message || String(error);
            if (msg.includes("no rows")) {
              msg = "Dataset does not exist.";
            }
            $toast.open({
              queue: true,
              duration: 5000,
              message: `Failed to retrieve dataset: ${msg}`,
              type: "error",
              position: "top",
            });
          });
      }
    },
    async confirmDuplicateUpload(args) {
      this.confirmMessage = `Destination dataset contains ${args.duplicate_count} ${args.duplicate_count === 1 ? 'image' : 'images'} with the same name. 
      ${args.duplicate_count === 1 ? 'This image' : 'These images'} will be ignored.`;
      this.confirmNote = "";
      this.confirmMessageHeader = 'Duplicate files';
      this.buttonText = "Confirm";
      this.buttonClass = null;
      this.confirmFunction = async () => { await this.handleConfirmDuplicateUpload(args.fullParams); };
      this.$refs.confirmModal.showModal();
    },
    async handleConfirmDuplicateUpload(params) {
      this.$refs.uploadImagesModal.handleUploadConfirmed({ ...params, ignoreDuplicate: true });
    },
    async handleEditSequenceDescription(payload) {
      const { sequenceId, description } = payload;
      try {
        const params = {
          sequence_id: sequenceId,
          description: description,
        };
        const resp = await this.dataConnect.updateSequenceDescription(params);
        if (resp && !resp.error) {
          // Update the local sequence object
          const idx = this.sequenceList.findIndex((s) => s.id === sequenceId);
          if (idx >= 0) {
            this.sequenceList[idx].description = description;
          }
          const $toast = useToast();
          $toast.open({
            queue: true,
            duration: 3000,
            message: 'Description updated successfully',
            type: 'success',
            position: 'top',
          });
        } else {
          throw new Error(resp?.error?.message || 'Failed to update description');
        }
      } catch (error) {
        const $toast = useToast();
        $toast.open({
          queue: true,
          duration: 5000,
          message: `Error: ${error.message}`,
          type: 'error',
          position: 'top',
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.gallery-wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
}

.gallery-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;
  position: relative;
}

.gallery-container.with-content {
  overflow: auto;
}

.gallery-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  gap: 20px;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #ffffff;
  z-index: 10;
  
  p {
    margin: 0;
    font-size: 16px;
    color: #666;
  }
}

.lds-ring {
  display: inline-block;
  position: relative;
  width: 80px;
  height: 80px;

  div {
    box-sizing: border-box;
    display: block;
    position: absolute;
    width: 64px;
    height: 64px;
    margin: 8px;
    border: 8px solid #1a1a1a;
    border-radius: 50%;
    animation: lds-ring 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
    border-color: #1a1a1a transparent transparent transparent;

    &:nth-child(1) {
      animation-delay: -0.45s;
    }
    &:nth-child(2) {
      animation-delay: -0.3s;
    }
    &:nth-child(3) {
      animation-delay: -0.15s;
    }
  }
}

@keyframes lds-ring {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.controls-bar {
  display: flex;
  flex-direction: row;
  padding: 10px 20px 10px 20px;
  align-items: center;
  border-bottom: 1px solid #c9c9c9;
  @include themify() {
    background: themed('color-ribbon');
  }
  justify-content: space-between;
}

#dataset-select {
  width: 300px;
}

.gallery-tabs {
  display: flex;
  gap: 0;
  height: 100%;
  align-items: stretch;
}

.gallery-tab {
  padding: 6px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
  border-bottom: 2px solid transparent;
  background: none;
  white-space: nowrap;
  @include themify() {
    color: themed('body-text-color-secondary');
  }

  &:hover {
    @include themify() {
      background: themed('card-list-hover-fill');
    }
  }

  &--active {
    border-bottom: 2px solid var(--color-primary);
    @include themify() {
      color: themed('body-text-color');
    }
  }
}

.gallery-actions {
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  height: 100%;
  width: 100%;

  &__toolbar {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__pagination {
    flex: 1;
    display: flex;
    justify-content: center;
  }

  span{
    font-weight: bold;
    font-size: 1.15rem;
  }

  button {
    background: white;
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    height: 26px;
    box-shadow: 1px 2px 4px 1px rgba(0, 0, 0, 0.2), 0 0 1px 1px rgba(0, 0, 0, 1);
    flex: 1 1 auto;
    padding: 4px 8px;
    gap: 2px;
    border-right: solid 1px rgba(0,0,0,0.5);
    max-width: fit-content;

    @include themify() {
      color: themed('body-text-color')
    }

    &.import {
      background: var(--button-color);
      color: var(--button-text-color);

      svg {
        margin-right: 5px;
      }
    }

    &:hover {
      background: rgba(var(--color-primary-100-rgb), 0.5);
    }

    &.delete:not([disabled]) {
      @include themify() {
        background: themed('button-delete');
        color: themed('button-delete-text');
      }
    }

    &.delete[disabled] {
      border: none;
    }

    &.delete:hover:not([disabled]) {
      @include themify() {
        background: $color-red-300;
        color: themed('button-delete-text');
      }
    }

    &.inactive {
      background: rgba(var(--color-disabled), 0.5) !important;
    }
  }
}

.actions-dropdown-button {
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 26px;
  padding: 0;
  box-shadow: 1px 2px 4px 1px rgba(0, 0, 0, 0.2), 0 0 1px 1px rgba(0, 0, 0, 1);
  @include themify() {
    color: themed('body-text-color')
  }
}

.actions-toggle-button {
  display: flex;
  align-items: center;
  height: 100%;
  flex: 1 1 auto;
  padding: 4px 8px;
  gap: 2px;
  border-right: solid 1px rgba(0,0,0,0.5);
  color: inherit;
  background: white;

  &:hover {
    box-shadow: 0 0 2px 1px rgba(var(--color-primary-rgb), 0.2) inset;
    background: rgba(var(--color-primary-100-rgb), 0.5);
  }

  &:active {
    opacity: 1;
  }

  &.active {
    @include themify() {
      box-shadow: 0 0 2px 1px rgba(var(--color-primary-rgb), 0.5) inset;
      background: rgba(var(--color-primary-100-rgb), 0.8);
    }
  }
}

.actions-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 10px;
  background: #e53935;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
}

.sort-by {
  display: flex;
  flex-direction: row;
  align-items: center;

  &__select {
    width: 130px;
  }

  &__reverse._invert {
    transform: rotate(180deg);
  }
}

#loading-visualization {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  background: rgba(255, 255, 255, 0.9);
}

.gallery-sequence-loading {
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  z-index: 101;
  background: rgba(255, 255, 255, 0.9);

  p {
    margin: 0;
    font-size: 16px;
    color: #666;
  }
}

.gallery-image-loading {
  position: fixed;
  left: 0;
  top: 0;
  height: 100vh;
  width: 100vw;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  z-index: 100;
  background: rgba(255, 255, 255, 0.9);

  p {
    margin: 0;
    font-size: 16px;
    color: #666;
  }
}

.lds-ring {
  /* change color here */
  color: var(--color-primary);
}
.lds-ring,
.lds-ring div {
  box-sizing: border-box;
}
.lds-ring {
  display: inline-block;
  position: relative;
  width: 80px;
  height: 80px;
}
.lds-ring div {
  box-sizing: border-box;
  display: block;
  position: absolute;
  width: 64px;
  height: 64px;
  margin: 8px;
  border: 8px solid currentColor;
  border-radius: 50%;
  animation: lds-ring 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
  border-color: currentColor transparent transparent transparent;
}
.lds-ring div:nth-child(1) {
  animation-delay: -0.45s;
}
.lds-ring div:nth-child(2) {
  animation-delay: -0.3s;
}
.lds-ring div:nth-child(3) {
  animation-delay: -0.15s;
}
@keyframes lds-ring {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

:deep(#gallery-page-selector button) {
  min-width: fit-content;
}

.select-all-warning {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 50px;
  background: var(--gray-300);
  color: var(--gray-600);
  font-weight: 400;
  gap: 12px;

  button {
    color: var(--button-link-color);
  }
}

.filter-drawer-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.3);
  z-index: 500;
  display: flex;
  justify-content: flex-end;
}

.filter-drawer {
  width: 300px;
  max-width: 100vw;
  height: 100%;
  display: flex;
  flex-direction: column;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.2);
  background: #f5f5f5;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid #c9c9c9;
    flex-shrink: 0;
  }

  &__content {
    flex: 1;
    overflow-y: auto;
    padding: 16px 20px;

    :deep(.filters) {
      flex-direction: column;
      flex-wrap: nowrap;
    }

    :deep(.filters__active-and-default-filters) {
      flex-direction: column;
      flex-wrap: nowrap;

      > * {
        width: 100%;
      }
    }

    :deep(.filter-button) {
      width: 100% !important;
      max-width: 100% !important;
    }
  }
}

.filter-drawer-enter-active,
.filter-drawer-leave-active {
  transition: opacity 0.25s ease;

  .filter-drawer {
    transition: transform 0.25s ease;
  }
}

.filter-drawer-enter-from,
.filter-drawer-leave-to {
  opacity: 0;

  .filter-drawer {
    transform: translateX(100%);
  }
}

@media (max-width: 480px) {
  .filter-drawer {
    width: 100vw;
  }
}
</style>
